/**
 * Underwriting Config Page
 * Main page component for managing underwriting configuration
 * Five tabs: Config, Missing Info, Denied, Checklist, Risk Analysis
 */

import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { showSuccess, showError, showApiError } from '@/lib/utils/notifications'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import {
  getMissingInfoTemplate,
  getDeniedInfoTemplate,
  getChecklistTemplate,
} from '../services/underwritingConfigService'
import { PageShell } from '@/components/layout/PageShell'
import { EntityEditTabBar, type EntityEditTabItem } from '@/components/ui/EntityEditTabBar'
import { ConfigTab } from '../components/tabs/ConfigTab'
import { MissingInfoTab } from '../components/tabs/MissingInfoTab'
import { DeniedInfoTab } from '../components/tabs/DeniedInfoTab'
import { ChecklistTab } from '../components/tabs/ChecklistTab'
import type {
  EmailTemplate,
  ChecklistTemplate,
} from '../types/underwritingConfig'

import type { UnderwritingConfigTabId } from '../types'

/**
 * Angular parity: in Angular the Missing Info tab is declared with `*ngIf="false"`, so its
 * tab button and editor are never rendered and its `submitMissingForm()` (POST
 * /api/email_templates/72) is unreachable. Keep the code in place but gate it off with this
 * flag — matching the source of truth. Flip to `true` only if Angular re-enables the tab.
 */
const SHOW_MISSING_INFO_TAB: boolean = false

const UW_TABS: EntityEditTabItem[] = [
  { id: 'config', label: 'Config', testId: 'underwriting-config-tab-config' },
  { id: 'missinginfo', label: 'Missing Info', testId: 'underwriting-config-tab-missinginfo' },
  { id: 'deniedinfo', label: 'Denied Reason', testId: 'underwriting-config-tab-deniedinfo' },
  { id: 'checklist', label: 'Checklist', testId: 'underwriting-config-tab-checklist' },
  { id: 'riskanalysis', label: 'Risk Analysis', testId: 'underwriting-config-tab-riskanalysis' },
]

export function UnderwritingConfigPage() {
  const [selectedTab, setSelectedTab] = useState<UnderwritingConfigTabId>('config')
  const [isLoading, setIsLoading] = useState(true)
  const [missingTemplate, setMissingTemplate] = useState<EmailTemplate | null>(null)
  const [deniedTemplate, setDeniedTemplate] = useState<EmailTemplate | null>(null)
  const [checklistTemplate, setChecklistTemplate] = useState<ChecklistTemplate | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadingRef = useRef(false)

  useEffect(() => {
    if (loadingRef.current) return
    loadingRef.current = true

    const loadTemplates = async () => {
      try {
        setError(null)
        setIsLoading(true)

        const [missingRes, deniedRes, checklistRes] = await Promise.allSettled([
          getMissingInfoTemplate(),
          getDeniedInfoTemplate(),
          getChecklistTemplate(),
        ])

        const errors: string[] = []

        if (missingRes.status === 'fulfilled') {
          setMissingTemplate(missingRes.value)
        } else {
          errors.push(extractErrorMessage(missingRes.reason) || 'Failed to load Missing Info template')
        }

        if (deniedRes.status === 'fulfilled') {
          setDeniedTemplate(deniedRes.value)
        } else {
          errors.push(extractErrorMessage(deniedRes.reason) || 'Failed to load Denied Info template')
        }

        if (checklistRes.status === 'fulfilled') {
          setChecklistTemplate(checklistRes.value)
        } else {
          errors.push(extractErrorMessage(checklistRes.reason) || 'Failed to load Checklist template')
        }

        if (errors.length > 0) {
          const combinedMessage = errors.join('; ')
          setError(combinedMessage)
          showError(combinedMessage)
        }
      } catch (err) {
        const message = extractErrorMessage(err) || 'Failed to load templates'
        setError(message)
        showError(message)
      } finally {
        setIsLoading(false)
      }
    }

    void loadTemplates()
  }, [])

  // Angular parity (`*ngIf="false"`): hide the Missing Info tab button while disabled.
  const visibleTabs = SHOW_MISSING_INFO_TAB
    ? UW_TABS
    : UW_TABS.filter((tab) => tab.id !== 'missinginfo')

  return (
    <PageShell title="UNDERWRITING CONFIGURATION">
      <div className="relative flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm w-full">
        <EntityEditTabBar
          tabs={visibleTabs}
          activeTab={selectedTab}
          onSelect={(id) => setSelectedTab(id as UnderwritingConfigTabId)}
          navTestId="underwriting-config-tabs"
          ariaLabel="Underwriting configuration sections"
        />

        <div className="min-h-[450px] bg-slate-50/50 p-4 sm:p-6">
          {selectedTab === 'config' && (
            <div id="panel-config" role="tabpanel" aria-labelledby="underwriting-config-tab-config">
              <ConfigTab isLoading={isLoading} />
            </div>
          )}

          {/*
            Keep template tabs mounted and use `hidden` for visibility (Angular-style).
            Conditional unmounting lost in-progress edits when switching tabs (e.g. delete
            a section on Missing Info, open Denied Reason, return — form reset from server).
          */}
          {/* Angular parity: Missing Info tab is `*ngIf="false"` — render only when enabled. */}
          {SHOW_MISSING_INFO_TAB && (
            <div
              id="panel-missinginfo"
              role="tabpanel"
              hidden={selectedTab !== 'missinginfo'}
              aria-labelledby="underwriting-config-tab-missinginfo"
            >
              <MissingInfoTab
                template={missingTemplate}
                isLoading={isLoading}
                onSuccess={() => {
                  showSuccess('Missing Info Successfully!')
                  getMissingInfoTemplate()
                    .then(setMissingTemplate)
                    .catch((e) => showError(e.message))
                }}
                onError={(e) => showApiError(e)}
              />
            </div>
          )}

          <div
            id="panel-deniedinfo"
            role="tabpanel"
            hidden={selectedTab !== 'deniedinfo'}
            aria-labelledby="underwriting-config-tab-deniedinfo"
          >
            <DeniedInfoTab
              template={deniedTemplate}
              isLoading={isLoading}
              onSuccess={() => {
                showSuccess('Denied reason template saved successfully!')
                getDeniedInfoTemplate()
                  .then(setDeniedTemplate)
                  .catch((e) => showError(e.message))
              }}
              onError={(e) => showApiError(e)}
            />
          </div>

          <div
            id="panel-checklist"
            role="tabpanel"
            hidden={selectedTab !== 'checklist'}
            aria-labelledby="underwriting-config-tab-checklist"
          >
            <ChecklistTab
              template={checklistTemplate}
              isLoading={isLoading}
              onSuccess={() => {
                showSuccess('Checklist template saved successfully!')
                getChecklistTemplate()
                  .then(setChecklistTemplate)
                  .catch((e) => showError(e.message))
              }}
              onError={(e) => showApiError(e)}
            />
          </div>

          {selectedTab === 'riskanalysis' && (
            <div
              id="panel-riskanalysis"
              role="tabpanel"
              aria-labelledby="underwriting-config-tab-riskanalysis"
            >
              <div className="row" />
            </div>
          )}
        </div>

        {error && (
          <div className="fixed top-5 right-5 z-[100] flex max-w-md items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 shadow-lg">
            <p className="m-0 text-sm font-medium text-red-600">{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="flex-shrink-0 text-red-500 hover:text-red-700 transition-colors"
              aria-label="Dismiss error"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </PageShell>
  )
}
